/**
 * Grand Quack Auto — duck animation system.
 *
 * Visual-only: waddle, body lean, head bob, wing flap, tail wag, idle breathing.
 * Runs in render() at display framerate for smooth animation.
 * Accesses duck children by name (cached on first call).
 *
 * Per research §2 "juice": squash-and-stretch, head bobbing via sine-wave,
 * wing flapping via group rotation.
 */

import * as THREE from 'three';
import { DUCK, DUCK_ANIM } from '../config';
import type { System, World } from '../types';
import { damp } from '../utils/mathUtils';

// Cached child references — looked up once, invalidated if duck changes.
let cachedDuck: THREE.Group | null = null;
let body: THREE.Object3D | null = null;
let head: THREE.Object3D | null = null;
let leftWing: THREE.Object3D | null = null;
let rightWing: THREE.Object3D | null = null;
let tail: THREE.Object3D | null = null;
let leftFoot: THREE.Object3D | null = null;
let rightFoot: THREE.Object3D | null = null;

// Animation state.
let phase = 0;
let bodyBaseY = 0;

function cacheRefs(duck: THREE.Group): void {
  cachedDuck = duck;
  body = duck.getObjectByName('body') ?? null;
  head = duck.getObjectByName('head') ?? null;
  leftWing = duck.getObjectByName('leftWing') ?? null;
  rightWing = duck.getObjectByName('rightWing') ?? null;
  tail = duck.getObjectByName('tail') ?? null;
  leftFoot = duck.getObjectByName('leftFoot') ?? null;
  rightFoot = duck.getObjectByName('rightFoot') ?? null;
  if (body) bodyBaseY = body.position.y;
}

export const duckAnimationSystem: System = {
  name: 'duckAnimation',
  render(dt: number, _alpha: number, world: World) {
    const duck = world.duck;
    if (duck !== cachedDuck) cacheRefs(duck);
    if (!body) return;

    const inputLen = Math.hypot(world.input.move.x, world.input.move.y);
    const isMoving = inputLen > 0.01;
    const speed = inputLen * DUCK.walkSpeed;

    // Advance phase while moving (faster movement = faster waddle).
    if (isMoving) phase += dt * speed;

    // ── Body bob (vertical bounce) ───────────────────────────────────
    if (isMoving) {
      body.position.y = bodyBaseY +
        Math.sin(phase * DUCK_ANIM.bobFrequency) * DUCK_ANIM.bobAmplitude;
    } else {
      // Idle breathing.
      body.position.y = bodyBaseY +
        Math.sin(world.time.elapsed * DUCK_ANIM.idleBobFrequency) * DUCK_ANIM.idleBobAmplitude;
    }

    // ── Waddle (side-to-side roll) ───────────────────────────────────
    const targetWaddle = isMoving
      ? Math.sin(phase * DUCK_ANIM.waddleFrequency) * DUCK_ANIM.waddleAmplitude
      : 0;
    duck.rotation.z = damp(duck.rotation.z, targetWaddle, 0.01, dt);

    // ── Forward lean ─────────────────────────────────────────────────
    const targetLean = isMoving ? DUCK_ANIM.forwardLean : 0;
    duck.rotation.x = damp(duck.rotation.x, targetLean, DUCK_ANIM.leanSmoothing, dt);

    // ── Head bob (independent, faster frequency) ─────────────────────
    if (head) {
      const headTarget = isMoving
        ? Math.sin(phase * DUCK_ANIM.headBobFrequency) * DUCK_ANIM.headBobAmplitude
        : Math.sin(world.time.elapsed * 1.5) * 0.02;
      head.rotation.x = damp(head.rotation.x, headTarget, 0.02, dt);
    }

    // ── Wing flap (mirrored, amplitude scales with speed) ────────────
    if (leftWing && rightWing) {
      const speedFactor = isMoving ? speed / DUCK.runSpeed : 0;
      const wingAngle = Math.sin(phase * DUCK_ANIM.wingFlapFrequency) *
        DUCK_ANIM.wingFlapAmplitude * speedFactor;
      leftWing.rotation.z = damp(leftWing.rotation.z, -wingAngle, 0.02, dt);
      rightWing.rotation.z = damp(rightWing.rotation.z, wingAngle, 0.02, dt);
    }

    // ── Tail wag ─────────────────────────────────────────────────────
    if (tail) {
      const wagTarget = isMoving
        ? Math.sin(phase * DUCK_ANIM.tailWagFrequency) * DUCK_ANIM.tailWagAmplitude
        : 0;
      tail.rotation.y = damp(tail.rotation.y, wagTarget, 0.02, dt);
    }

    // ── Foot waddle (alternate left/right) ───────────────────────────
    if (leftFoot && rightFoot) {
      const footPhase = isMoving
        ? Math.sin(phase * DUCK_ANIM.waddleFrequency) * 0.02
        : 0;
      leftFoot.position.y = 0.015 + footPhase;
      rightFoot.position.y = 0.015 - footPhase;
    }
  },
};
